import {jointRoomCamera} from './photo-joint-camera';
import type {JointCorners} from '../../../../shared/roomCornerPreference';
import {Matrix4,Vector3,Box3,Sphere,PerspectiveCamera} from 'three';
export type PhotoPoint={x:number;y:number};
export type RoomWalls={corner:PhotoPoint;left:PhotoPoint;right:PhotoPoint;upper:PhotoPoint;upperLeft:PhotoPoint;upperRight:PhotoPoint;ceiling?:boolean;floorOnly?:boolean;jointCorners?:JointCorners};
export type WallCamera={focal:number;basis:Matrix4;floorPlanePerMeter?:number;approximate?:boolean};
export const wallKeys=['corner','left','right','upper','upperLeft','upperRight'] as const;
export function validRoomWalls(value:unknown):value is RoomWalls{
 if(!value||typeof value!=='object')return false;
 return wallKeys.every(key=>{const p=(value as RoomWalls)[key];return p&&Number.isFinite(p.x)&&Number.isFinite(p.y)&&p.x>=0&&p.x<=100&&p.y>=0&&p.y<=100});
}
export function defaultRoomWalls(corner:PhotoPoint):RoomWalls{
 return {corner:{...corner},left:{x:5,y:95},right:{x:95,y:95},upper:{x:corner.x,y:20},upperLeft:{x:5,y:5},upperRight:{x:95,y:5}};
}
function intersection(a:PhotoPoint,b:PhotoPoint,c:PhotoPoint,d:PhotoPoint){
 const ab={x:b.x-a.x,y:b.y-a.y},cd={x:d.x-c.x,y:d.y-c.y};
 const cross=ab.x*cd.y-ab.y*cd.x;
 if(Math.abs(cross)<1e-7||Math.hypot(ab.x,ab.y)<.04||Math.hypot(cd.x,cd.y)<.04)return null;
 const t=((c.x-a.x)*cd.y-(c.y-a.y)*cd.x)/cross;
 return {x:a.x+t*ab.x,y:a.y+t*ab.y};
}
/** Recover a perspective camera from two perpendicular wall vanishing points.
 * Coordinates use photo height as the unit, so portrait/landscape/resizing agree.
 * Assumes a rectilinear photo, central principal point and perpendicular walls.
 * Invalid/ambiguous geometry stays uncalibrated instead of approving a default angle.
 */
export function wallCamera(walls:RoomWalls,aspect:number):WallCamera|null{
 if(!validRoomWalls(walls)||!Number.isFinite(aspect)||aspect<=0)return null;
 if(walls.floorOnly&&walls.jointCorners)return jointRoomCamera(walls.jointCorners,aspect,walls.corner);
 if(walls.floorOnly)return floorPreviewCamera(walls,aspect);
 const p=(v:PhotoPoint)=>({x:(v.x/100-.5)*aspect,y:v.y/100-.5});
 const corner=p(walls.corner),upper=p(walls.upper),left=p(walls.left),right=p(walls.right);
 const l=intersection(corner,left,upper,p(walls.upperLeft)),r=intersection(corner,right,upper,p(walls.upperRight));
 if(!l||!r)return null;
 const f2=-(l.x*r.x+l.y*r.y);
 if(!Number.isFinite(f2)||f2<.08||f2>100)return null;
 const focal=Math.sqrt(f2);
 const direction=(vp:PhotoPoint,end:PhotoPoint)=>{
  const v=new Vector3(vp.x,-vp.y,-focal).normalize();
  const dx=focal*v.x+corner.x*v.z,dy=-focal*v.y+corner.y*v.z;
  if(dx*(end.x-corner.x)+dy*(end.y-corner.y)<0)v.negate();return v;
 };
 // Existing model: X is the short end, Z is the long side; rear contact is min X/min Z.
 const x=direction(r,right),z=direction(l,left),y=new Vector3().crossVectors(z,x).normalize();
 const upX=focal*y.x+corner.x*y.z,upY=-focal*y.y+corner.y*y.z;
 const observed={x:upper.x-corner.x,y:upper.y-corner.y};
 const cosine=(upX*observed.x+upY*observed.y)/(Math.hypot(upX,upY)*Math.hypot(observed.x,observed.y));
 if(!Number.isFinite(cosine)||cosine<.94||Math.abs(x.dot(z))>.01)return null;
 const basis=new Matrix4().makeBasis(x,y,z);
 // A known floor-to-ceiling height supplies metric scale; wall angles alone cannot.
 const delta={x:upper.x-corner.x,y:upper.y-corner.y};
 const norm=delta.x*delta.x+delta.y*delta.y;
 const depthPerMeter=(delta.x*(upper.x*y.z+focal*y.x)+delta.y*(upper.y*y.z-focal*y.y))/norm;
 const floorPlanePerMeter=depthPerMeter>0&&walls.ceiling?y.dot(roomAnchorPosition(walls.corner,aspect,focal,depthPerMeter)):undefined;
 return {focal,basis,...Number.isFinite(floorPlanePerMeter)?{floorPlanePerMeter}:{}};
}

/** Translate the model around the chosen contact; do not shear or stretch its geometry. */
export function roomAnchorPosition(pin:PhotoPoint,aspect:number,focal:number,depth:number){
 return new Vector3((pin.x/100-.5)*aspect*depth/focal,(.5-pin.y/100)*depth/focal,-depth);
}

/** Shared by the interactive renderer and visual regression renders. */
export function fitRoomBunk(view:PerspectiveCamera,bounds:Box3,contact:Vector3,rotation:Matrix4,placement:PhotoPoint&{scale:number},aspect:number,focal:number){
 view.fov=2*Math.atan(.5/focal)*180/Math.PI;view.aspect=aspect;view.updateProjectionMatrix();view.updateMatrixWorld();
 const rotatedContact=contact.clone().applyMatrix4(rotation),offsets:Vector3[]=[];
 for(const x of [bounds.min.x,bounds.max.x])for(const y of [bounds.min.y,bounds.max.y])for(const z of [bounds.min.z,bounds.max.z])offsets.push(new Vector3(x,y,z).applyMatrix4(rotation).sub(rotatedContact));
 const radius=bounds.getBoundingSphere(new Sphere()).radius;
 const targetHeight=Math.max(.1,Math.min(1.6,placement.scale/100*aspect*bounds.getSize(new Vector3()).y/(2*radius*1.08)));
 const projectAt=(depth:number)=>{const anchor=roomAnchorPosition(placement,aspect,focal,depth);return offsets.map(p=>p.clone().add(anchor).project(view));};
 let lo=Math.max(.1,...offsets.map(p=>p.z+.1)),hi=200;
 for(let i=0;i<40;i++){const mid=(lo+hi)/2,ys=projectAt(mid).map(p=>p.y);if((Math.max(...ys)-Math.min(...ys))/2>targetHeight)lo=mid;else hi=mid;}
 return roomAnchorPosition(placement,aspect,focal,(lo+hi)/2).sub(rotatedContact);
}

/** Intersect the pin's camera ray with the calibrated floor. The model retains its
 * dimensions at 100%; pinching uniformly adjusts the visual scale around that foot.
 * Camera position/scale no longer depends on viewport fill, bed type or orbit angle.
 */
export function roomFloorDepth(pin:PhotoPoint,aspect:number,calibration:WallCamera,ceilingInches:number){
 if((calibration.floorPlanePerMeter===undefined&&!calibration.approximate)||!Number.isFinite(ceilingInches)||ceilingInches<=0)return null;
 const up=new Vector3().setFromMatrixColumn(calibration.basis,1);
 const ray=roomAnchorPosition(pin,aspect,calibration.focal,1);
 const floorOffset=calibration.approximate?-60*.0254:calibration.floorPlanePerMeter!*(ceilingInches*.0254);
 const depth=floorOffset/up.dot(ray);
 return Number.isFinite(depth)&&depth>.1&&depth<=100?depth:null;
}
export function roomScaleTransform(view:PerspectiveCamera,contact:Vector3,rotation:Matrix4,placement:PhotoPoint&{scale:number},aspect:number,calibration:WallCamera,ceilingInches:number){
 const depth=roomFloorDepth(placement,aspect,calibration,ceilingInches);
 if(depth===null)return null;
 view.fov=2*Math.atan(.5/calibration.focal)*180/Math.PI;view.aspect=aspect;view.updateProjectionMatrix();view.updateMatrixWorld();
 const scale=placement.scale/100;
 const anchor=roomAnchorPosition(placement,aspect,calibration.focal,depth);
 return {scale,position:anchor.sub(contact.clone().applyMatrix4(rotation).multiplyScalar(scale))};
}


/** Photo previews use the owner's 60-inch standing camera reference. Wall lines
 * still provide lens and orientation; they must not invent an elevated camera.
 */
export function standingRoomCamera(camera:WallCamera,ceilingInches:number):WallCamera{
 return {...camera,floorPlanePerMeter:-60/Math.max(1,ceilingInches)};
}

/** Floor-only visual alignment. Lens and standing eye height are references,
 * not measurements. Fit orientation to the observed floor rays and seam. */
export function floorPreviewCamera(walls:RoomWalls,aspect:number):WallCamera|null {
 if(!validRoomWalls(walls)||!Number.isFinite(aspect)||aspect<=0)return null;
 const focal=.9*Math.max(1,aspect),cx=(walls.corner.x/100-.5)*aspect,cy=walls.corner.y/100-.5;
 const targets=[walls.right,walls.upper,walls.left].map(p=>new Vector3((p.x-walls.corner.x)*aspect,-(p.y-walls.corner.y),0).normalize());
 if(walls.left.x>=walls.corner.x-10||walls.right.x<=walls.corner.x+10||walls.upper.y>=walls.corner.y-10)return null;
 function axes(yaw:number,pitch:number,roll:number){
  const x=new Vector3(Math.cos(yaw),-Math.sin(pitch)*Math.sin(yaw),Math.cos(pitch)*Math.sin(yaw));
  const z=new Vector3(-Math.sin(yaw),-Math.sin(pitch)*Math.cos(yaw),Math.cos(pitch)*Math.cos(yaw));
  const y=new Vector3().crossVectors(z,x).normalize();
  return [x,y,z].map(v=>v.applyAxisAngle(new Vector3(0,0,1),roll));
 }
 const loss=(q:number[])=>axes(q[0],q[1],q[2]).reduce((sum,v,i)=>{
  const ray=new Vector3(focal*v.x+cx*v.z,focal*v.y-cy*v.z,0).normalize();
  return sum+(1-ray.dot(targets[i]));
 },0);
 let best=[Math.PI/4,.15,0],error=Infinity;
 for(const yaw of [.25,.65,1,1.3])for(const pitch of [-.25,0,.25,.5]){
  let q=[yaw,pitch,0],e=loss(q);
  for(let step=.2;step>.0001;step*=.5)for(let round=0;round<20;round++){
   let improved=false;
   for(let axis=0;axis<3;axis++)for(const sign of [-1,1]){
    const next=[...q];next[axis]+=sign*step;
    if(next[0]<.05||next[0]>1.52||Math.abs(next[1])>.8||Math.abs(next[2])>.4)continue;
    const n=loss(next);if(n<e){q=next;e=n;improved=true;}
   }
   if(!improved)break;
  }
  if(e<error){best=q;error=e;}
 }
 if(error>.015)return null;
 const [x,y,z]=axes(best[0],best[1],best[2]);
 return {focal,basis:new Matrix4().makeBasis(x,y,z),approximate:true};
}

